import { Head, Link } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface Vendor {
  id: number
  display_name: string
}

interface Document {
  id: number
  display_name: string
  title: string | null
  document_date: string | null
  filename: string | null
  file_type: string | null
  download_url: string | null
  created_at: string
}

interface Props {
  vendor: Vendor
  documents: Document[]
}

export default function Index({ vendor, documents }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Subcontractors", href: "/company/vendors" },
    { title: vendor.display_name, href: `/company/vendors/${vendor.id}` },
    { title: "Documents", href: `/company/vendors/${vendor.id}/documents` },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${vendor.display_name} - Documents`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Documents for {vendor.display_name}
          </h1>
          <Link
            href={`/company/vendors/${vendor.id}/documents/new`}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add Document
          </Link>
        </div>

        {documents.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Title
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Date
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  File
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Uploaded
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/company/vendors/${vendor.id}/documents/${doc.id}`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue"
                    >
                      {doc.title ?? doc.display_name}
                    </Link>
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {formatDate(doc.document_date)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {doc.download_url ? (
                      <a
                        href={doc.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-hpi-active-blue hover:underline"
                      >
                        {doc.filename}
                      </a>
                    ) : (
                      doc.filename
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {formatDate(doc.created_at)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/company/vendors/${vendor.id}/documents/${doc.id}/edit`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue"
                    >
                      Edit
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No documents found.</p>
        )}
      </div>
    </AppLayout>
  )
}
